import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../auth/auth.service';
import { environment } from '../../../environments/environment';

/**
 * Informationen zur aktuell aktiven Impersonation.
 *
 * Kommt vom Backend (GET /api/impersonation/status) und entspricht 1:1
 * dem dortigen ImpersonationStatus-DTO. Feldnamen orientieren sich am API-Contract.
 */
export interface ImpersonationInfo {
  /** UserId des impersonierten Users. */
  impersonierterUserId: string;
  /** Anzeigename fuer die Impersonation-Bar. */
  anzeigename: string;
  /** Rolle des impersonierten Users. */
  rolle: string;
  /** Tenant-Name des impersonierten Users. */
  tenantName: string;
  /** Ablaufzeitpunkt der Impersonation (ISO-String, Backend liefert UTC). */
  laeuftAbAm: string;
}

/**
 * Service fuer SystemAdmin-Impersonation.
 *
 * Der Impersonation-Zustand wird ausschliesslich im Backend (DB) gehalten –
 * das Frontend ist Zustandslos und spiegelt nur den Backend-Status.
 *
 * Vorteil: Impersonation gilt tab-uebergreifend (Deep-Links aus WhatsApp
 * oeffnen neue Tabs, in denen die Impersonation weiterhin aktiv ist).
 *
 * Flow:
 *  1. `starten(userId)` → POST /api/impersonation/start → reload → /api/users/me
 *     liefert jetzt den impersonierten User.
 *  2. `beenden()` → POST /api/impersonation/end → reload → /api/users/me
 *     liefert wieder den SystemAdmin.
 *  3. `statusLaden()` wird einmalig nach dem Login aufgerufen (vom AuthService),
 *     damit die Impersonation-Bar sofort sichtbar ist.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationService implements OnDestroy {

  private readonly apiUrl = `${environment.apiUrl}/api/impersonation`;

  private readonly _info$ = new BehaviorSubject<ImpersonationInfo | null>(null);

  /** Observable auf die aktuelle Impersonation-Info (null = keine Impersonation). */
  readonly info$ = this._info$.asObservable();

  private readonly subs = new Subscription();

  constructor(
    private authService: AuthService,
    private http: HttpClient
  ) {
    // Jedes Mal wenn ein User erfolgreich geladen wurde (App-Start nach Login,
    // Seiten-Refresh, Login-Redirect), aktuellen Impersonation-Status abholen.
    // Dadurch ist die Impersonation-Bar auch in neuen Browser-Tabs sofort sichtbar.
    this.subs.add(
      this.authService.userGeladen$.subscribe(() => {
        this.statusLaden().subscribe({
          error: () => { /* Status optional – Fehler schluckt der Service */ }
        });
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** True wenn aktuell eine Impersonation aktiv ist. */
  get aktiv(): boolean {
    return this._info$.value !== null;
  }

  /** UserId des impersonierten Users (null wenn keine Impersonation). */
  get zielUserId(): string | null {
    return this._info$.value?.impersonierterUserId ?? null;
  }

  /** Vollstaendige Impersonation-Info (null wenn keine Impersonation). */
  get zielInfo(): ImpersonationInfo | null {
    return this._info$.value;
  }

  /**
   * Laedt den aktuellen Impersonation-Status vom Backend und
   * aktualisiert `info$`. Liefert den Status auch als Observable zurueck.
   *
   * Backend antwortet mit `null` wenn keine Impersonation aktiv ist
   * oder mit einem ImpersonationStatus-Objekt.
   */
  statusLaden(): Observable<ImpersonationInfo | null> {
    return this.http
      .get<ImpersonationInfo | null>(`${this.apiUrl}/status`)
      .pipe(tap(status => this._info$.next(status)));
  }

  /**
   * Startet die Impersonation fuer einen bestimmten User.
   *
   * Ruft das Backend auf, leert den AuthService-Cache und laedt die Seite
   * neu – damit `/api/users/me` den impersonierten User liefert.
   * Nach dem Reload zeigt die App den impersonierten Kontext.
   */
  starten(userId: string): Observable<ImpersonationInfo> {
    return this.http
      .post<ImpersonationInfo>(`${this.apiUrl}/start`, { userId })
      .pipe(
        tap(status => {
          this._info$.next(status);
          this.authService.clearUser();
          // URL auf /dashboard setzen bevor reload – das Dashboard ist fuer
          // alle Rollen erreichbar und vermeidet "Kein Zugriff" wenn die
          // aktuelle Seite fuer die Ziel-Rolle gesperrt ist.
          history.replaceState(null, '', '/dashboard');
          window.location.reload();
        })
      );
  }

  /**
   * Beendet die aktive Impersonation.
   *
   * Ruft das Backend auf, leert den AuthService-Cache und laedt die Seite
   * neu – damit `/api/users/me` wieder den SystemAdmin liefert.
   */
  beenden(): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/end`, {}).pipe(
      tap(() => {
        this._info$.next(null);
        this.authService.clearUser();
        window.location.reload();
      })
    );
  }
}
